/**
 * Copyright (c) Aleksei Volkov and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useSnackbar } from 'notistack'
import { useRoutes } from 'hooks/index'
import { useModalbar } from 'modules/Modalbar'
import { useTranslation } from 'react-i18next'
import { Typography } from '@material-ui/core'

/**
 * @function useResErrors
 *  hook
 *  @example const {standardCheck} = useResErrors()
 *  @return {Object}  {standardCheck()}
 */
const useResErrors = () => {
  const { unAuthByStatus, goPrevPage } = useRoutes()
  const { callModalbar } = useModalbar()
  const { t } = useTranslation()

  /**
   * @function standardCheck
   * checkOnError
   * @param {object|array} res
   * @param {function} onSuccess
   * @param {object}options
   * @param {string} options.textErr
   * @param {function} options.onFail
   * @param {function} options.prevFailAction
   * @param {function|null} options.onErrCustom
   *  @example standardCheck(res,onSuccess(),{ textErr: 'error'})
   */
  const standardCheck = (
    res,
    onSuccess,
    options = {
      textErr: ' ',
      onFail: () => {},
      prevFailAction: () => {},
      onErrCustom: null
    }
  ) => {
    const { textErr, onFail, onErrCustom, prevFailAction } = options
    if ([200, 201].includes(res?.status) && !res.hasOwnProperty('err')) {
      try {
        onSuccess && onSuccess(res)
      } catch (e) {
        console.error(e)
        callModalbar(
          t('errors.inFunction', { path: 'hooks/useResErrors.js' }),
          e.toString(),
          {
            variant: 'error',
            onCloseAction: goPrevPage
          }
        )
      }
    } else {
      try {
        const status = res && res.hasOwnProperty('status') ? res.status : ''
        unAuthByStatus(status)
        const msg = res && res.hasOwnProperty('msg') ? res.msg : ''
        prevFailAction()
        if (!onErrCustom) {
          callModalbar(
            `${textErr || res.err} ${textErr && status ? status : ''}`,
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant={'body1'}
                style={{
                  background: '#475467',
                  borderRadius: 5,
                  overflowY: 'auto',
                  color: '#fff',
                  maxWidth: 367,
                  maxHeight: 320,
                  overflowWrap: 'break-word',
                  padding: 10
                }}
              >{`${msg || ''}`}</Typography>
              {res?.path && (
                <Typography
                  variant={'subtitle1'}
                  color={'textSecondary'}
                >{`Path: ${res?.path || ''} `}</Typography>
              )}
            </div>,
            {
              variant: 'error',
              onCloseAction: onFail
            }
          )
        } else {
          onErrCustom &&
            typeof onErrCustom === 'function' &&
            onErrCustom(status)
        }
      } catch (e) {
        console.error(e)

        callModalbar(
          t('errors.inFunction', { path: 'hooks/useResErrors.js' }),
          e.toString(),
          {
            variant: 'error',
            onCloseAction: goPrevPage
          }
        )
      }
    }
  }
  return {
    standardCheck
  }
}
export default useResErrors
